import { Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges } from '@angular/core';
import { Subscription } from 'rxjs';

import * as services from 'app/services';
import * as models from 'app/models';

import {
  EventUpdateComponent,
  EventAddComponent,
} from '../../../dialogs';
import { MatDialog } from '@angular/material/dialog';
import { Timestamp } from '@angular/fire/firestore';
import { Unsubscribe, onSnapshot } from 'firebase/firestore';
import { ActivatedRoute, Params, Router } from '@angular/router';

export interface EventColumn {
  /** Persisted identifier — matches the legacy AG Grid header names so saved layouts survive. */
  key: string;
  header: string;
  /** EventRow field used for sorting/CSV. */
  field: string;
  sortable: boolean;
  type: 'priority' | 'dayOfWeek' | 'date' | 'text' | 'notes' | 'tags' | 'minutes' | 'percent';
  minWidth?: string;
}

export interface EventRow {
  event: models.Event;
  uid: string;
  dateMs: number;
  phase: string;
  description: string;
  notes: Array<string>;
  assignedTo: string;
  completedBy: string;
  estimatedTime: number;
  timeSpent: number;
  efficacy: number;
  complianceStage: string;
  zones: string;
  tags: Array<string>;
  searchText: string;
}

@Component({
    selector: 'app-event-list',
    templateUrl: './event-list.component.html',
    styleUrls: ['./event-list.component.scss'],
    standalone: false
})
export class EventListComponent implements OnInit, OnChanges, OnDestroy {

  //Input/Output
  @Input() collectionType: string; //db collection target in firebase (either 'harvests' or 'groups')
  @Input() collectionName: string;
  @Input() collectionId: string;
  @Input() company: models.Company;

  dbEvent: models.DbEvent;
  @Output() eventListChange = new EventEmitter();
  events: models.Event[];

  //table state
  allRows: EventRow[] = [];
  filteredRows: EventRow[] = [];
  private baselineCompleted = new Map<string, boolean>();

  //toolbar state
  searchTerm: string = '';
  selectedPhase: string = null;
  hideCompleted: boolean = false;
  phaseOptions: Array<string> = [];

  //variables
  loading: boolean = true;
  users: Array<models.User> = [];
  teams: Array<models.Team> = [];
  pageLoaded: boolean = false;
  eventId: string;

  //subscriptions
  eventSub: Unsubscribe;
  querySub: Subscription;

  //harvest
  harvest: models.Harvest;
  template: models.Template;
  zones: models.Zone[];

  get canCompleteEvent(): boolean {
    return this.claimsService.userRole?.permissions?.includes(models.Permissions.completeEvent);
  }

  get isTaskGroup(): boolean {
    return this.collectionType == 'groups';
  }

  private allColumns: EventColumn[] = [
    { key: 'Priority', header: 'Priority', field: 'highPriority', sortable: false, type: 'priority' },
    { key: 'Day of Week', header: 'Day', field: 'dateMs', sortable: true, type: 'dayOfWeek' },
    { key: 'Date', header: 'Date', field: 'dateMs', sortable: true, type: 'date', minWidth: '110px' },
    { key: 'Phase', header: 'Phase', field: 'phase', sortable: true, type: 'text' },
    { key: 'Event Description', header: 'Event Description', field: 'description', sortable: true, type: 'text', minWidth: '220px' },
    { key: 'Notes', header: 'Notes', field: 'notes', sortable: false, type: 'notes', minWidth: '200px' },
    { key: 'Assigned to', header: 'Assigned To', field: 'assignedTo', sortable: true, type: 'text' },
    { key: 'Completed By', header: 'Completed By', field: 'completedBy', sortable: true, type: 'text' },
    { key: 'Est. Time (Min)', header: 'Est. Time', field: 'estimatedTime', sortable: true, type: 'minutes' },
    { key: 'Time Spent', header: 'Time Spent', field: 'timeSpent', sortable: true, type: 'minutes' },
    { key: 'Quality Tracker', header: 'Quality', field: 'efficacy', sortable: true, type: 'percent' },
    { key: 'Compliance Stage', header: 'Compliance Stage', field: 'complianceStage', sortable: true, type: 'text' },
    { key: 'Zone', header: 'Zone', field: 'zones', sortable: true, type: 'text' },
    { key: 'Tags', header: 'Tags', field: 'tags', sortable: false, type: 'tags', minWidth: '160px' },
  ];

  /** Columns visible for this collection type, in the user's persisted order. */
  columns: EventColumn[] = [];

  //dirty tracking — diffed by event uid, never by array position (p-table sorts mutate the array)
  get pendingChanges(): EventRow[] {
    return this.allRows.filter(r => this.baselineCompleted.get(r.uid) != (r.event.completed == true));
  }

  private lastPendingLogged: string = null;
  get hasPendingChanges(): boolean {
    const pending = this.pendingChanges;
    //diagnostic: log which rows differ from the baseline whenever the set changes
    const sig = pending.map(r => `${r.uid}:${r.event.completed == true}`).join('|');
    if (pending.length > 0 && sig !== this.lastPendingLogged) {
      this.lastPendingLogged = sig;
      console.log('[event-list] pending task changes:', pending.map(r => ({
        uid: r.uid,
        description: r.description,
        baselineCompleted: this.baselineCompleted.get(r.uid),
        currentCompleted: r.event.completed == true,
      })));
    }
    if (pending.length === 0) {
      this.lastPendingLogged = null;
    }
    return pending.length > 0;
  }

  isDirty(row: EventRow): boolean {
    return this.baselineCompleted.get(row.uid) != (row.event.completed == true);
  }

  //check-all state (applies to the currently visible rows)
  get allVisibleChecked(): boolean {
    return this.filteredRows.length > 0 && this.filteredRows.every(r => r.event.completed);
  }
  get someVisibleChecked(): boolean {
    return this.filteredRows.some(r => r.event.completed) && !this.allVisibleChecked;
  }

  get eventList(): Array<string> {
    let el: Array<string> = null;
    if (this.collectionType == 'harvests') {
      el = this.appStorage.eventListColumns;
    } else if (this.collectionType == 'groups') {
      el = this.appStorage.taskGroupEventListColumns;
    }
    return el;
  }
  set eventList(val: Array<string>) {
    if (this.collectionType == 'harvests') {
      this.appStorage.eventListColumns = val;
    } else if (this.collectionType == 'groups') {
      this.appStorage.taskGroupEventListColumns = val;
    }
  }

  constructor(
    private userService: services.UserService
    , private eventService: services.EventService
    , private helperService: services.HelperService
    , private dialog: MatDialog
    , private claimsService: services.ClaimsService
    , private notifierService: services.NotifierService
    , private appStorage: services.AppStorage
    , private teamService: services.TeamService
    , private harvestService: services.HarvestService
    , private templateService: services.TemplateService
    , private zoneService: services.ZoneService
    , private activatedRoute: ActivatedRoute
    , private router: Router
  ) { }

  async ngOnInit(): Promise<void> {
    this.listenForQuery();
    this.setColumns();

    //get companyId
    let companyId = this.helperService.currentCompanyId;
    this.users = await this.userService.getUsersForCompanyWithCaching(companyId, true, true);
    this.teams = await this.getTeams();

    if (this.collectionType == 'harvests') {
      let harvestResponse = await this.harvestService.get(this.collectionId).get().toPromise();
      this.harvest = harvestResponse.data();
      this.harvest.uid = harvestResponse.id;

      if (this.harvest.templateId) {
        this.template = await this.templateService.get(this.harvest.templateId);
      }

      await this.getDbZones();
    }

    this.getEvents();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.collectionId && !changes.collectionId.firstChange) {
      this.getEvents();
    }
  }

  ngOnDestroy(): void {
    if (this.eventSub != null) {
      this.eventSub();
    }
    if (this.querySub != null) {
      this.querySub.unsubscribe();
    }
  }

  listenForQuery() {
    this.querySub = this.activatedRoute.queryParams.subscribe(params => {
      this.eventId = params.evt;

      if (this.eventId != null) {
        this.autoOpenEventPopup(this.eventId);
      }
    });
  }

  async getDbZones() {
    let dbZone = await this.zoneService.getDbZone();
    if (dbZone == null) {
      return;
    }
    this.zones = dbZone.list;
  }

  async getTeams(): Promise<models.Team[]> {
    let teams: models.Team[] = [];
    let teamResponse = await (await this.teamService.getAll().get()).toPromise();
    teamResponse.docs.forEach(doc => {
      let team: models.Team = doc.data();
      team.uid = doc.id;
      teams.push(team);
    });
    return teams;
  }

  /** Order the visible columns from the persisted layout (legacy AG Grid header-name format). */
  setColumns() {
    let available = this.allColumns.filter(c => {
      if (this.isTaskGroup && ['Phase', 'Compliance Stage', 'Zone'].includes(c.key)) {
        return false;
      }
      return true;
    });

    let stored = this.eventList;
    if (stored == null || stored.length == 0) {
      this.columns = available;
      return;
    }

    //legacy quirks: '' was the checkbox column, 'Messages' was renamed to 'Notes'
    let normalized = stored
      .filter(name => name !== '')
      .map(name => name == 'Messages' ? 'Notes' : name);

    let ordered: EventColumn[] = [];
    normalized.forEach(name => {
      let found = available.find(c => c.key == name);
      if (found != null && !ordered.includes(found)) {
        ordered.push(found);
      }
    });
    //new/unknown columns land at the end instead of disappearing
    available.forEach(c => {
      if (!ordered.includes(c)) {
        ordered.push(c);
      }
    });

    this.columns = ordered;
  }

  onColReorder(event: any) {
    let cols: EventColumn[] = event.columns ?? this.columns;
    this.columns = [...cols];
    this.eventList = this.columns.map(c => c.key);
  }

  getEvents() {
    //switching collections (harvest -> harvest, group -> group) reuses this
    //component, so drop the previous listener or both keep writing to the rows
    if (this.eventSub != null) {
      this.eventSub();
      this.eventSub = null;
    }

    if (this.company.eventsSplit) {
      this.getEventsSplit();
    } else {
      this.getEventsOld();
    }
  }

  getEventsSplit() {
    this.eventSub = onSnapshot(this.eventService.getSplit(this.collectionId, this.collectionType), (eventResponse) => {
      this.loading = true;

      this.events = [];
      eventResponse.forEach(eventData => {
        let event: models.Event = eventData.data();
        event.uid = eventData.id;
        this.events.push(event);
      });

      this.setRows(this.events);
      this.eventListChange.next(this.events);
    });
  }

  getEventsOld() {
    this.eventSub = onSnapshot(this.eventService.get(this.collectionId, this.collectionType), eventResponse => {
      this.loading = true;

      for (let i = 0; i < eventResponse.size; i++) {
        const eventData = eventResponse.docs[i];
        let dbEvent: models.DbEvent = eventData.data();
        dbEvent.uid = eventData.id;
        this.dbEvent = dbEvent;

        this.dbEvent.list.forEach((event, index) => {
          event.uid = `${this.dbEvent.uid}::${index}`;
        });
      }

      this.setRows(this.dbEvent?.list ?? []);
      this.eventListChange.next(this.dbEvent?.list ?? []);

      if (this.eventId != null) {
        this.autoOpenEventPopup(this.eventId);
      }
    });
  }

  private setRows(events: models.Event[]) {
    let sorted: models.Event[] = this.helperService.sortArrayByNumberField(events, 'startDateTime');

    this.allRows = sorted.map(event => this.buildRow(event));
    this.baselineCompleted = new Map(this.allRows.map(r => [r.uid, r.event.completed == true]));

    //phase filter options, in schedule order
    let phases: string[] = [];
    this.allRows.forEach(r => {
      if (r.phase && !phases.includes(r.phase)) {
        phases.push(r.phase);
      }
    });
    this.phaseOptions = phases;
    if (this.selectedPhase != null && !phases.includes(this.selectedPhase)) {
      this.selectedPhase = null;
    }

    this.applyFilters();
    this.loading = false;
    this.pageLoaded = true;
  }

  private buildRow(event: models.Event): EventRow {
    let notes: string[] = (event.discussions ?? [])
      .filter(d => d.discussionType == models.DiscussionType.Note)
      .map(d => d.content);

    const assignedUserIds = event.assignedUserIds || [];
    const assignedTeamIds = event.assignedTeamIds || [];
    const userNames = this.users.filter(u => assignedUserIds.includes(u.id)).map(u => u.displayName);
    const teamNames = this.teams.filter(t => assignedTeamIds.includes(t.uid)).map(t => t.name);
    const assignedTo = this.helperService.concatToSentence(teamNames.concat(userNames));

    let completedBy: string = null;
    if (event.completed) {
      const discussion = [...(event.discussions ?? [])].reverse()
        .find(d => d.discussionType === models.DiscussionType.CompletedStatus);
      if (discussion) {
        completedBy = this.users.find(u => u.id === discussion.userId)?.displayName ?? null;
      }
    }

    const complianceStage = this.getComplianceStage(event.phase);
    const zones = this.getZones(event.phase);
    const plainNotes = notes.map(n => this.plainText(n));

    const searchText = [
      event.description,
      event.phase,
      assignedTo,
      completedBy,
      complianceStage,
      zones,
      ...(event.tags ?? []),
      ...plainNotes,
    ].filter(s => s != null && s !== '').join(' ').toLowerCase();

    return {
      event: event,
      uid: event.uid,
      dateMs: event.startDateTime != null ? event.startDateTime.seconds * 1000 : 0,
      phase: event.phase,
      description: event.description,
      notes: notes,
      assignedTo: assignedTo,
      completedBy: completedBy,
      estimatedTime: this.toNumber(event.estimatedTime),
      timeSpent: this.toNumber(event.timeSpent),
      efficacy: this.toNumber(event.efficacy),
      complianceStage: complianceStage,
      zones: zones,
      tags: event.tags ?? [],
      searchText: searchText,
    };
  }

  private toNumber(val: any): number {
    if (val == null || val === '') {
      return null;
    }
    let num = Number(val);
    return isNaN(num) ? null : num;
  }

  private plainText(val: string): string {
    if (val == null) {
      return '';
    }
    return this.helperService.isHTMLString(val) ? this.helperService.extractVisibleTextFromHTML(val) : val;
  }

  applyFilters() {
    const term = (this.searchTerm || '').trim().toLowerCase();
    this.filteredRows = this.allRows.filter(r => {
      if (this.hideCompleted && r.event.completed && !this.isDirty(r)) {
        return false;
      }
      if (this.selectedPhase != null && r.phase !== this.selectedPhase) {
        return false;
      }
      if (term !== '' && !r.searchText.includes(term)) {
        return false;
      }
      return true;
    });
  }

  selectPhase(phase: string) {
    this.selectedPhase = this.selectedPhase === phase ? null : phase;
    this.applyFilters();
  }

  toggleAll(checked: boolean) {
    this.filteredRows.forEach(r => r.event.completed = checked);
  }

  getComplianceStage(phase: string) {
    if (phase == null || this.template == null) {
      return null;
    }

    return this.template.phases?.find(i => i.name == phase)?.compliancePhase;
  }

  getZones(phase: string): string {
    if (phase == null || this.zones == null) {
      return null;
    }

    let harvestPhase = this.harvest.phases?.find(i => i.phaseName == phase);
    let zoneIds = harvestPhase?.strains?.flatMap(i => i.zones).filter(i => i != null).map(i => i.zoneId);
    if (zoneIds == null) {
      return '';
    }
    let zones = this.zoneService.getAllTopLevel(this.zones, zoneIds);
    return zones.map(i => i.name).join(', ');
  }

  eventClick(row: EventRow) {
    let event: models.Event = row.event;
    let start = this.helperService.timestampToDate(event.startDateTime);
    let harvestId = this.collectionId;
    const isTaskGroupEvent = this.isTaskGroup;

    let data: any = {
      event: event,
      start: start,
      assignToGroup: isTaskGroupEvent,
      harvestId: harvestId,
      eventList: this.dbEvent != null ? this.dbEvent.list : this.events,
      saveInDialog: this.helperService.currentCompany?.eventsSplit || false
    };

    let dialogRef = this.dialog.open(EventUpdateComponent, {
      width: '800px',
      data: data
    });

    dialogRef.afterClosed().subscribe(async result => {
      if (!result) {
        return;
      }
      if (result.save) {
        //perform save
        let dbEvent: models.DbEvent = {
          uid: this.dbEvent.uid,
          list: result.eventList
        };
        await this.eventService.save(isTaskGroupEvent, this.collectionId, dbEvent);
        if (result.automaticallyCompletedEvent) {
          this.notifierService.success('All subtasks are complete, event has been automatically marked as completed');
        }
      }
      else if (result.delete) {
        let list: models.Event[] = result.eventList;
        let index = list.indexOf(result.event);

        if (index > -1) {
          result.eventList.splice(index, 1);
        }

        let dbEvent: models.DbEvent = {
          uid: this.dbEvent.uid,
          list: result.eventList
        };
        await this.eventService.save(isTaskGroupEvent, this.collectionId, dbEvent);
      }
    });
  }

  addEvent() {
    let data: any = {};
    if (this.isTaskGroup) {
      data = {
        groupId: this.collectionId,
        addFromGroupDetail: true,
      };
    } else {
      data = { harvestId: this.collectionId };
    }
    data.saveInDialog = this.helperService.currentCompany?.eventsSplit || false;

    let dialogRef = this.dialog.open(EventAddComponent, {
      panelClass: 'med-width-dialog',
      data: data,
      autoFocus: true
    });

    dialogRef.afterClosed().subscribe(async result => {
      if (result && result.save) {
        //perform save
        let assignToGroup: boolean = result.assignToGroup;
        let harvest: models.Harvest = result.harvest;
        let group: models.Group = result.group;

        let events: Array<models.Event> = result.events;
        let dbEvents: Array<models.Event> = [];

        events.forEach(event => {
          let dbEvent: models.Event = event;

          dbEvent.color = assignToGroup ? group.color : harvest.color;

          if (assignToGroup) {
            delete dbEvent.harvestName;
            dbEvent.groupName = group.name;
          } else {
            delete dbEvent.groupName;
            dbEvent.harvestName = harvest.name;
          }

          dbEvents.push(dbEvent);
        });

        await this.eventService.add(assignToGroup, assignToGroup ? group.uid : harvest.uid, dbEvents);
      }
    });
  }

  async saveChanges() {
    const isTaskGroup = this.isTaskGroup;
    const successText = 'Successfully saved Events in ' + (isTaskGroup ? 'Group ' : 'Harvest ');

    const changedRows = this.pendingChanges;

    changedRows.forEach(row => {
      if (row.event.discussions == null) {
        row.event.discussions = [];
      }

      let newDis: models.Discussion = {
        discussionType: models.DiscussionType.CompletedStatus,
        userId: this.claimsService.currentUserId(),
        timestamp: Timestamp.now(),
        content: row.event.completed ? 'true' : 'false',
      };
      row.event.discussions.push(newDis);
    });

    if (this.company.eventsSplit) {
      await this.eventService.saveListSplit(isTaskGroup, changedRows.map(r => r.event));
    } else {
      //rows wrap the same Event object references as dbEvent.list, so the
      //mutations above are already reflected in the document we save
      await this.eventService.save(isTaskGroup, this.collectionId, this.dbEvent);
    }

    //re-baseline so the pending bar clears immediately (the snapshot will also re-fire)
    this.baselineCompleted = new Map(this.allRows.map(r => [r.uid, r.event.completed == true]));

    this.notifierService.success(successText + `'` + this.collectionName + `'`);
  }

  undoChanges() {
    this.pendingChanges.forEach(row => {
      row.event.completed = this.baselineCompleted.get(row.uid);
    });
    this.applyFilters();
  }

  exportAsCSV() {
    const esc = (s: any) => '"' + String(s ?? '').replace(/"/g, '""') + '"';

    const cellText = (row: EventRow, col: EventColumn): string => {
      switch (col.type) {
        case 'priority': return row.event.highPriority ? 'High Priority' : '';
        case 'dayOfWeek': return row.dateMs ? new Date(row.dateMs).toLocaleDateString('en-US', { weekday: 'short' }) : '';
        case 'date': return row.event.startDateTime != null ? this.helperService.timestampToDateString(row.event.startDateTime) : '';
        case 'notes': return row.notes.map(n => this.plainText(n)).join(' | ');
        case 'tags': return row.tags.join(' | ');
        case 'minutes': return row[col.field] != null ? `${row[col.field]} minutes` : '';
        case 'percent': return row[col.field] != null ? `${row[col.field]}%` : '';
        default: return row[col.field] ?? '';
      }
    };

    const header = ['Completed', ...this.columns.map(c => esc(c.key))].join(',');
    const lines = [header];
    this.filteredRows.forEach(row => {
      lines.push([
        row.event.completed ? 'Yes' : 'No',
        ...this.columns.map(c => esc(cellText(row, c)))
      ].join(','));
    });

    const blob = new Blob([lines.join('\r\n')], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${this.collectionName}_TaskExport.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }

  autoOpenEventPopup(eventId: string) {
    let foundRow = this.allRows.find(i => i.event.id == eventId);

    if (foundRow == null) {
      return;
    }

    this.eventClick(foundRow);

    const queryParams: Params = { evt: null };

    this.router.navigate(
      [],
      {
        relativeTo: this.activatedRoute,
        queryParams,
        queryParamsHandling: 'merge', // remove to replace all query params by provided
      });
  }

}
